import { useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, Text, chakra } from '@chakra-ui/react';
import { Bell, BellOff, Info } from 'lucide-react';
import { MUTE_DURATIONS, describeMute, type MuteDuration } from '@/utils/chatMute';

type Props = {
  muted: boolean;
  /** Null means "until I turn it back on"; undefined when not muted. */
  mutedUntil: string | null | undefined;
  onMute: (duration: MuteDuration) => void;
  onUnmute: () => void;
  onOpenDetails: () => void;
};

/**
 * The chat header's overflow menu.
 *
 * Built the same way EventSelector is -- a positioned Box with its own outside-click and
 * Escape handling -- rather than pulling in a menu primitive the codebase does not
 * otherwise use.
 */
export function ChatMenu({ muted, mutedUntil, onMute, onUnmute, onOpenDetails }: Props) {
  const [open, setOpen] = useState(false);
  const [showDurations, setShowDurations] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Reopening should always land on the top level, never on a half-finished choice.
  useEffect(() => {
    if (!open) setShowDurations(false);
  }, [open]);

  const close = () => setOpen(false);

  const itemStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: 2.5,
    w: 'full',
    px: 3,
    py: 2.5,
    fontSize: 'sm',
    color: 'volyx.primary',
    textAlign: 'start' as const,
    cursor: 'pointer',
    bg: 'transparent',
    _hover: { bg: 'volyx.lightGray' },
  };

  return (
    <Box position="relative" ref={ref} flexShrink={0}>
      <Button
        type="button"
        variant="ghost"
        colorPalette="gray"
        size="sm"
        p={1}
        aria-label="Chat menu"
        aria-haspopup="menu"
        aria-expanded={open}
        fontSize="xl"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        ⋮
      </Button>

      {open ? (
        <Box
          role="menu"
          position="absolute"
          top="calc(100% + 6px)"
          right={0}
          zIndex={20}
          minW="232px"
          bg="volyx.bg"
          borderWidth={1}
          borderColor="volyx.borderLight"
          borderRadius="lg"
          shadow="lg"
          overflow="hidden"
          py={1}
        >
          {showDurations ? (
            <>
              <Text px={3} pt={1.5} pb={2} fontSize="xs" color="volyx.textSecondary">
                Mute this conversation
              </Text>
              {MUTE_DURATIONS.map((option) => (
                <chakra.button
                  key={option.value}
                  type="button"
                  role="menuitem"
                  {...itemStyles}
                  onClick={() => {
                    onMute(option.value);
                    close();
                  }}
                >
                  {option.label}
                </chakra.button>
              ))}
            </>
          ) : (
            <>
              {muted ? (
                <chakra.button
                  type="button"
                  role="menuitem"
                  {...itemStyles}
                  onClick={() => {
                    onUnmute();
                    close();
                  }}
                >
                  <Bell size={16} />
                  <Flex direction="column" align="flex-start" gap={0}>
                    <Text fontSize="sm">Unmute</Text>
                    <Text fontSize="xs" color="volyx.textSecondary">
                      {describeMute(mutedUntil)}
                    </Text>
                  </Flex>
                </chakra.button>
              ) : (
                <chakra.button
                  type="button"
                  role="menuitem"
                  {...itemStyles}
                  onClick={() => setShowDurations(true)}
                >
                  <BellOff size={16} />
                  Mute notifications
                </chakra.button>
              )}

              <chakra.button
                type="button"
                role="menuitem"
                {...itemStyles}
                onClick={() => {
                  onOpenDetails();
                  close();
                }}
              >
                <Info size={16} />
                Event details
              </chakra.button>
            </>
          )}
        </Box>
      ) : null}
    </Box>
  );
}
